import { useMemo } from 'react';
import { useNextSpeciesSummaryQuery } from 'src/app/portfolio/views/next/state/api/nextSpeciesGraphSlice';
import type { NextSpeciesSummary } from 'src/app/portfolio/views/next/state/api/nextSpeciesQuery';
import { useSpeciesSummaryCardsQuery } from '../state/api/portfolioGraphSlice';
import type { SpeciesSummaryCardsData } from '../state/api/portfolioQuery';
import { usePortfolioSpeciesSource } from '../state/portfolioSpeciesSource';

/**
 * Hardcoded in the Go resolver for both cards rather than derived from any species,
 * so the grid path states the same constant instead of dropping the chips.
 */
const REPORTING_FRAMEWORKS = ['TNFD', 'CSRD', 'GRI'];

type Args = {
    customerId: string;
    filters?: { name: string; values: string[] }[];
};

/**
 * The portfolio summary's species cards, from whichever source the route declared.
 *
 * The grid names almost every field differently and has no per-portfolio run date, so
 * it is mapped into the projection's card shape here. The cards are shared components
 * and stay unaware of which source filled them.
 */
export const useSpeciesSummaryCards = ({ customerId, filters }: Args) => {
    const isGrid = usePortfolioSpeciesSource() === 'grid';
    const args = { customerId, ...(filters?.length ? { filters } : {}) };

    // Both called so the Rules of Hooks hold; only the active one is unskipped.
    const projection = useSpeciesSummaryCardsQuery(args, { skip: !customerId || isGrid });
    const grid = useNextSpeciesSummaryQuery(args, { skip: !customerId || !isGrid });

    // Memoised because the summary page assembles a useMemo from this. A fresh object
    // each render restarts that chain indefinitely.
    const fromGrid = useMemo(() => (grid.currentData ? toCardData(grid.currentData) : null), [grid.currentData]);

    if (isGrid) {
        return { data: fromGrid, isFetching: grid.isFetching };
    }
    return { data: projection.currentData ?? null, isFetching: projection.isFetching };
};

const toCardData = (summary: NextSpeciesSummary): SpeciesSummaryCardsData => ({
    numberSites: summary.siteCount,
    totalCount: summary.totalSpeciesCount,
    // The grid has no per-portfolio run date. Provenance is the snapshot the figures
    // were read from, which is what replaces lastPredictionRun across this epic.
    lastPredictionRun: summary.snapshot,
    redListSpecies: {
        siteCount: summary.redListSiteCount,
        count: summary.redListSpeciesCount,
        frameworks: REPORTING_FRAMEWORKS,
        threatenedCount: summary.threatenedSpeciesCount,
        // The chart reads iucnRedListCategory; the grid calls it category.
        chartData: summary.iucn.map(entry => ({ iucnRedListCategory: entry.category, count: entry.count })),
    },
    invasiveSpecies: {
        count: summary.invasiveSpeciesCount,
        siteCount: summary.invasiveSiteCount,
        frameworks: REPORTING_FRAMEWORKS,
    },
});
